import { portalApi, RepositoryFileRead } from '../../../../store/generated/admin/repository-files';
import { emptySplitApi } from '../../../../store/emptyApi';

export type ResourceCategoryRead = {
  id: number;
  name?: string | null;
  slug?: string | null;
  description?: string | null;
  icon?: string | null;
  color?: string | null;
  category_group?: number | null;
  sort_order?: number | null;
  docs_count?: number;
  videos_count?: number;
  links_count?: number;
};

export type RepositoryFileVoteRow = {
  id?: number;
  employee_id?: number;
  employee_first_name?: string;
  employee_last_name?: string;
  employee_email?: string;
  vote: 1 | -1;
  feedback_notes?: string | null;
  created_at?: string;
};

export enum RepositoryApiTags {
  RepositoryFiles = 'repository-files',
  FileUploads = 'file-uploads',
  ResourceCategories = 'resource-categories',
}

export const taggedApi = portalApi.enhanceEndpoints({
  addTagTypes: Object.values(RepositoryApiTags),
});

const organizationApi = taggedApi.enhanceEndpoints({
  addTagTypes: [RepositoryApiTags.RepositoryFiles],
  endpoints: {
    apiAdminV1RepositoryRepositoryFilesList: {
      providesTags: [RepositoryApiTags.RepositoryFiles],
    },
    apiAdminV1RepositoryRepositoryFilesCreate: {
      invalidatesTags: [RepositoryApiTags.RepositoryFiles, RepositoryApiTags.ResourceCategories],
    },
    apiAdminV1RepositoryRepositoryFilesCreateUrlCreate: {
      invalidatesTags: [RepositoryApiTags.RepositoryFiles, RepositoryApiTags.ResourceCategories],
    },
    apiAdminV1RepositoryRepositoryFilesDestroy: {
      invalidatesTags: [RepositoryApiTags.RepositoryFiles, RepositoryApiTags.ResourceCategories],
    },
    apiAdminV1RepositoryRepositoryFilesUpdate: {
      invalidatesTags: [RepositoryApiTags.RepositoryFiles, RepositoryApiTags.ResourceCategories],
    },
    apiAdminV1RepositoryRepositoryFilesPartialUpdate: {
      invalidatesTags: [RepositoryApiTags.RepositoryFiles, RepositoryApiTags.ResourceCategories],
    },
  },
});

const taggedEmptyApi = emptySplitApi.enhanceEndpoints({
  addTagTypes: Object.values(RepositoryApiTags),
});

// Inject repository file endpoints into the authenticated emptySplitApi
// so they get auth headers, org context, and token refresh automatically.
const repositoryFileApi = taggedEmptyApi.injectEndpoints({
  endpoints: builder => ({
    getFileUploads: builder.query<RepositoryFileRead[], {category_id?: number} | void>({
      query: params => ({
        url: '/api/portal/v1/repository-files/',
        method: 'GET',
        params: params ?? undefined,
      }),
      providesTags: [RepositoryApiTags.FileUploads],
    }),

    getFileUpload: builder.query<any, string>({
      query: id => ({
        url: `/api/portal/v1/repository-files/${id}/`,
        method: 'GET',
      }),
      providesTags: [RepositoryApiTags.FileUploads],
    }),

    getFileUploadVotes: builder.query<RepositoryFileVoteRow[], string>({
      query: id => ({
        url: `/api/portal/v1/repository-files/${id}/votes/`,
        method: 'GET',
      }),
      providesTags: [RepositoryApiTags.FileUploads],
    }),

    voteFileUpload: builder.mutation({
      query: ({ id, vote, feedback_notes, employee_id, guestToken }: { id: string | number; vote: number; feedback_notes?: string; employee_id?: number; guestToken?: string }) => {
        const request: any = {
          url: `/api/portal/v1/repository-files/${id}/vote/`,
          method: 'POST',
          body: { vote, feedback_notes, employee_id },
        };
        if (guestToken) {
          request.headers = {
            Authorization: `Token ${guestToken}`,
          };
        }
        return request;
      },
      invalidatesTags: [RepositoryApiTags.FileUploads],
    }),

    createFileUpload: builder.mutation({
      query: data => ({
        url: '/api/portal/v1/repository-files/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [RepositoryApiTags.FileUploads],
    }),

    updateFileUpload: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/portal/v1/repository-files/${id}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: [RepositoryApiTags.FileUploads],
    }),

    deleteFileUpload: builder.mutation({
      query: id => ({
        url: `/api/portal/v1/repository-files/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [RepositoryApiTags.FileUploads],
    }),

    retryProcessing: builder.mutation({
      query: id => ({
        url: `/api/portal/v1/repository-files/${id}/retry_processing/`,
        method: 'POST',
      }),
      invalidatesTags: [RepositoryApiTags.FileUploads],
    }),

    regenerateEmbeddings: builder.mutation({
      query: id => ({
        url: `/api/portal/v1/repository-files/${id}/regenerate_embeddings/`,
        method: 'POST',
      }),
      invalidatesTags: [RepositoryApiTags.FileUploads],
    }),
    getResourceCategories: builder.query<ResourceCategoryRead[], void>({
      query: () => ({
        url: '/api/admin/v1/repository/resource-categories/',
        method: 'GET',
      }),
      providesTags: [RepositoryApiTags.ResourceCategories],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetFileUploadsQuery,
  useGetFileUploadQuery,
  useGetFileUploadVotesQuery,
  useCreateFileUploadMutation,
  useUpdateFileUploadMutation,
  useDeleteFileUploadMutation,
  useRetryProcessingMutation,
  useRegenerateEmbeddingsMutation,
  useVoteFileUploadMutation,
  useGetResourceCategoriesQuery,
} = repositoryFileApi;

export default organizationApi;
